// src/owner/pages/OwnerSubscription.jsx
import { useState, useEffect } from "react";
import OwnerLayout from "../layout/OwnerLayout";
import { API_BASE_URL } from "../../config/api";

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (document.getElementById("razorpay-script")) return resolve(true);
    const script = document.createElement("script");
    script.id = "razorpay-script";
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload  = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

const OwnerSubscription = () => {
  const [sub,          setSub]          = useState(null);
  const [plans,        setPlans]        = useState([]);
  const [upi,          setUpi]          = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null); // plan object user picks
  const [txnId,        setTxnId]        = useState("");
  const [method,       setMethod]       = useState("GPay");
  const [submitting,   setSubmitting]   = useState(false);
  const [rzpLoading,   setRzpLoading]   = useState(false);
  const [msg,          setMsg]          = useState({ type: "", text: "" });
  const [showRenew,    setShowRenew]    = useState(false);

  const token = localStorage.getItem("token");

  const loadSub = async () => {
    setLoading(true);
    try {
      const [subRes, plansRes, upiRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/subscription/my`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_BASE_URL}/api/plans/public`),
        fetch(`${API_BASE_URL}/api/settings/upi`),
      ]);
      const subData   = await subRes.json();
      const plansData = await plansRes.json();
      const upiData   = await upiRes.json();
      setSub(subData);
      // Only show paid plans (exclude trial plan)
      const paidPlans = Array.isArray(plansData) ? plansData.filter(p => p.slug !== "trial") : [];
      setPlans(paidPlans);
      setUpi(upiData);
      // Auto-select first plan if only one
      if (paidPlans.length === 1) setSelectedPlan(paidPlans[0]);
    } catch { }
    setLoading(false);
  };

  useEffect(() => { loadSub(); }, []);

  // ── Razorpay online payment ───────────────────────────────
  const handleRazorpayPayment = async () => {
    if (!selectedPlan) { setMsg({ type: "error", text: "Please select a plan first." }); return; }
    setRzpLoading(true);
    setMsg({ type: "", text: "" });
    try {
      const loaded = await loadRazorpayScript();
      if (!loaded) {
        setMsg({ type: "error", text: "Failed to load Razorpay. Check your internet connection." });
        setRzpLoading(false);
        return;
      }

      const res  = await fetch(`${API_BASE_URL}/api/razorpay/create-order`, {
        method:  "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ planSlug: selectedPlan.slug }),
      });
      const data = await res.json();
      if (!res.ok) {
        setMsg({ type: "error", text: data.message || "Could not initiate payment." });
        setRzpLoading(false);
        return;
      }

      const options = {
        key:         data.keyId,
        amount:      data.amount,
        currency:    data.currency,
        name:        "FraudCheck FDS",
        description: `${selectedPlan.name} — 1 Year Subscription`,
        order_id:    data.orderId,
        prefill:     { name: data.name, email: data.email },
        theme:       { color: "#4318FF" },
        handler: async (response) => {
          const verifyRes = await fetch(`${API_BASE_URL}/api/razorpay/verify`, {
            method:  "POST",
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            body:    JSON.stringify({ ...response, planSlug: selectedPlan.slug }),
          });
          const verifyData = await verifyRes.json();
          if (verifyRes.ok) {
            setMsg({ type: "success", text: "Payment successful! Your account is now active." });
            localStorage.setItem("subStatus", "active");
            loadSub();
          } else {
            setMsg({ type: "error", text: verifyData.message || "Payment verification failed." });
          }
        },
        modal: { ondismiss: () => setRzpLoading(false) },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (response) => {
        setMsg({ type: "error", text: `Payment failed: ${response.error.description}` });
        setRzpLoading(false);
      });
      rzp.open();
    } catch {
      setMsg({ type: "error", text: "Something went wrong. Please try again." });
    }
    setRzpLoading(false);
  };

  // ── Manual UPI / Cash submission ─────────────────────────
  const handleSubmit = async () => {
    if (!selectedPlan)           { setMsg({ type: "error", text: "Please select a plan first." }); return; }
    if (!txnId.trim())           { setMsg({ type: "error", text: "Please enter your Transaction ID." }); return; }
    if (txnId.trim().length < 6) { setMsg({ type: "error", text: "Transaction ID looks too short." }); return; }

    setSubmitting(true); setMsg({ type: "", text: "" });
    try {
      const res = await fetch(`${API_BASE_URL}/api/subscription/upload-proof`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ paymentMethod: method, transactionId: txnId.trim(), planSlug: selectedPlan.slug }),
      });
      const data = await res.json();
      if (res.ok) {
        setMsg({ type: "success", text: "Transaction ID submitted! Admin will activate within 12 hours." });
        setTxnId(""); setShowRenew(false);
        loadSub();
      } else {
        setMsg({ type: "error", text: data.message || "Submission failed." });
      }
    } catch { setMsg({ type: "error", text: "Network error. Try again." }); }
    setSubmitting(false);
  };

  if (loading) return (
    <OwnerLayout>
      <div style={{ padding: 40, textAlign: "center", color: "#a3aed0" }}>Loading...</div>
    </OwnerLayout>
  );

  const status    = sub?.subscription?.status || "trial";
  const expiry    = sub?.subscription?.expiryDate;
  const days      = sub?.daysRemaining;
  const isPending = status === "pending";
  const isActive  = status === "active";
  const isExpired = status === "expired" || sub?.isExpired;
  const isTrial   = status === "trial";

  const statusConfig = {
    active:  { color: "#16a34a", bg: "#dcfce7", icon: "✅", text: "Active"               },
    expired: { color: "#dc2626", bg: "#fee2e2", icon: "❌", text: "Expired"              },
    pending: { color: "#d97706", bg: "#fef3c7", icon: "⏳", text: "Payment Under Review" },
    trial:   { color: "#0891b2", bg: "#ecfeff", icon: "🔵", text: "Free Trial"           },
  };
  const sc = statusConfig[status] || statusConfig.trial;

  const currentPlanSlug = sub?.subscription?.plan || "trial";
  const currentPlan     = plans.find(p => p.slug === currentPlanSlug);

  const gstEnabled = upi?.gstEnabled || false;
  const gstPercent = upi?.gstPercent || 18;

  // Price for selected plan
  const planBase  = selectedPlan ? (selectedPlan.price || upi?.amount || 300) : 0;
  const gstAmt    = gstEnabled ? Math.round(planBase * gstPercent / 100) : 0;
  const planTotal = planBase + gstAmt;

  const showUpgradeSection = isTrial || isExpired || (isActive && showRenew);

  // Plan card colors
  const planColors = ["#4318FF", "#16a34a", "#d97706", "#dc2626", "#7c3aed"];

  return (
    <OwnerLayout>
      <div className="dashboard-header">
        <p className="welcome-text">Account</p>
        <h2 className="company-name">My Subscription</h2>
      </div>

      <div className="owner-dashboard-wrapper">

        {/* ── Global message ── */}
        {msg.text && (
          <div style={{
            padding: "14px 18px", borderRadius: 12, marginBottom: 16,
            fontWeight: 700, fontSize: 14,
            background: msg.type === "error" ? "#fee2e2" : "#dcfce7",
            color:      msg.type === "error" ? "#dc2626" : "#16a34a",
            border: `1.5px solid ${msg.type === "error" ? "#fecaca" : "#bbf7d0"}`,
          }}>
            {msg.type === "error" ? "❌ " : "✅ "}{msg.text}
          </div>
        )}

        {/* ── Status Card ── */}
        <div style={{ background: sc.bg, borderRadius: 16, padding: 24, marginBottom: 20, border: `1.5px solid ${sc.color}30` }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 28 }}>{sc.icon}</span>
              <div>
                <div style={{ fontSize: 18, fontWeight: 800, color: sc.color }}>{sc.text}</div>
                <div style={{ fontSize: 12, color: sc.color, opacity: 0.8, marginTop: 2 }}>
                  Plan: {sub?.subscription?.plan || "trial"}
                  {sub?.subscription?.amount ? ` • ₹${sub.subscription.amount}` : ""}
                </div>
              </div>
            </div>

            {isActive && days !== null && days <= 30 && days > 0 && (
              <button onClick={() => setShowRenew(!showRenew)}
                style={{ padding: "8px 18px", background: "#4318FF", color: "#fff", border: "none", borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                🔄 Renew Now
              </button>
            )}
          </div>

          {expiry && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 14 }}>
              <div style={{ background: "rgba(255,255,255,0.6)", borderRadius: 8, padding: "10px 14px" }}>
                <div style={{ fontSize: 11, color: sc.color, fontWeight: 600, marginBottom: 2 }}>EXPIRES ON</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: "#1b2559" }}>
                  {new Date(expiry).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
                </div>
              </div>
              <div style={{ background: "rgba(255,255,255,0.6)", borderRadius: 8, padding: "10px 14px" }}>
                <div style={{ fontSize: 11, color: sc.color, fontWeight: 600, marginBottom: 2 }}>DAYS REMAINING</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: days <= 0 ? "#dc2626" : days <= 30 ? "#d97706" : "#16a34a" }}>
                  {days === null ? "—" : days <= 0 ? "Expired" : `${days} days`}
                </div>
              </div>
            </div>
          )}

          {isPending && (
            <div style={{ marginTop: 12, padding: "10px 14px", background: "rgba(255,255,255,0.7)", borderRadius: 8, fontSize: 13, color: "#92400e" }}>
              ⏳ Transaction ID submitted. Admin will verify and activate within 12 hours.
              {sub?.subscription?.transactionId && (
                <div style={{ marginTop: 6, fontWeight: 700 }}>TXN: {sub.subscription.transactionId}</div>
              )}
            </div>
          )}

          {isTrial && (
            <div style={{ marginTop: 12, padding: "10px 14px", background: "rgba(255,255,255,0.7)", borderRadius: 8, fontSize: 13, color: "#0891b2" }}>
              You are on the <b>Free Trial</b>. Report unlimited frauds for free.
              {sub?.searchAllowance?.limit > 0 && ` Includes ${sub.searchAllowance.limit} fraud searches per day.`}
              {sub?.searchAllowance?.limit === 0 && " Fraud searches are not included in this plan."}
              {" "}Upgrade for more searches, alerts, and exports.
            </div>
          )}
        </div>

        {/* ── Expiry warnings ── */}
        {isActive && days !== null && days <= 30 && days > 0 && (
          <div style={{ background: "#fef3c7", borderRadius: 14, padding: 16, marginBottom: 20, border: "1.5px solid #fde68a" }}>
            <div style={{ fontWeight: 700, color: "#92400e", marginBottom: 4 }}>⚠️ Subscription Expiring Soon!</div>
            <div style={{ color: "#92400e", fontSize: 13 }}>
              Expires in <b>{days} days</b>. Renew now to keep your search access uninterrupted.
            </div>
          </div>
        )}

        {isExpired && (
          <div style={{ background: "#fee2e2", borderRadius: 14, padding: 16, marginBottom: 20, border: "1.5px solid #fecaca" }}>
            <div style={{ fontWeight: 700, color: "#dc2626", marginBottom: 4 }}>❌ Subscription Expired</div>
            <div style={{ color: "#dc2626", fontSize: 13 }}>Your subscription has expired. Renew below to regain search access.</div>
          </div>
        )}

        {/* ── Active plan features ── */}
        {currentPlan && isActive && (
          <div style={{ background: "#fff", borderRadius: 16, padding: 20, marginBottom: 20, border: "1.5px solid #e0e5f2", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: "#1b2559", marginBottom: 12 }}>
              📋 Your Plan — {currentPlan.name}
            </h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              {[
                { label: "Reports/month", value: currentPlan.features?.maxReportsPerMonth === -1 ? "Unlimited" : currentPlan.features?.maxReportsPerMonth },
                { label: "Searches/day",  value: currentPlan.features?.maxSearchesPerDay  === -1 ? "Unlimited" : currentPlan.features?.maxSearchesPerDay  },
                { label: "Export CSV",    value: currentPlan.features?.canExportCSV    ? "✅ Yes" : "❌ No" },
                { label: "Alerts",        value: currentPlan.features?.canReceiveAlerts ? "✅ Yes" : "❌ No" },
                { label: "Report Images", value: currentPlan.features?.canViewReportImages ? "✅ Yes" : "❌ No" },
                { label: "Bulk Import",   value: currentPlan.features?.canBulkImport ? "✅ Yes" : "❌ No" },
              ].map(f => (
                <div key={f.label} style={{ background: "#f9faff", borderRadius: 8, padding: "8px 12px" }}>
                  <div style={{ fontSize: 11, color: "#a3aed0", marginBottom: 2 }}>{f.label}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#1b2559" }}>{f.value}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── UPGRADE / RENEW SECTION ── */}
        {showUpgradeSection && plans.length > 0 && (
          <div style={{ background: "#fff", borderRadius: 20, padding: 24, marginBottom: 20, border: "1.5px solid #ede9fe", boxShadow: "0 4px 24px rgba(67,24,255,0.08)" }}>

            <h3 style={{ fontSize: 16, fontWeight: 800, color: "#1b2559", marginBottom: 4 }}>
              {isActive ? "🔄 Renew Your Plan" : "🚀 Choose Your Plan"}
            </h3>
            <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 20, marginTop: 0 }}>
              Compare the search and report limits and features included with each plan.
            </p>

            {/* ── Plan Cards — auto-generated from DB, future-proof ── */}
            <div style={{ display: "grid", gridTemplateColumns: plans.length === 1 ? "1fr" : "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, marginBottom: 24 }}>
              {plans.map((plan, i) => {
                const color     = plan.color || planColors[i % planColors.length];
                const base      = plan.price || 0;
                const gst       = gstEnabled ? Math.round(base * gstPercent / 100) : 0;
                const total     = base + gst;
                const isSelected = selectedPlan?.slug === plan.slug;
                return (
                  <div key={plan._id} onClick={() => { setSelectedPlan(plan); setMsg({ type: "", text: "" }); }}
                    style={{
                      borderRadius: 14, padding: 18, cursor: "pointer", transition: "all 0.2s",
                      border: `2px solid ${isSelected ? color : "#e0e5f2"}`,
                      background: isSelected ? `${color}08` : "#f9faff",
                      boxShadow: isSelected ? `0 4px 16px ${color}25` : "none",
                      position: "relative",
                    }}>
                    {isSelected && (
                      <div style={{ position: "absolute", top: 10, right: 10, background: color, color: "#fff", fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20 }}>
                        SELECTED
                      </div>
                    )}
                    <div style={{ fontSize: 13, fontWeight: 800, color, marginBottom: 4 }}>{plan.name}</div>
                    <div style={{ fontSize: 28, fontWeight: 900, color: "#1b2559", marginBottom: 2 }}>₹{total}</div>
                    <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 12 }}>
                      per {plan.billingCycle || "year"}
                      {gstEnabled && ` (incl. ${gstPercent}% GST)`}
                    </div>
                    {plan.description && (
                      <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 10, lineHeight: 1.5 }}>{plan.description}</div>
                    )}
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      {[
                        ["🔍", plan.features?.maxSearchesPerDay  === -1 ? "Unlimited searches" : `${plan.features?.maxSearchesPerDay || 0} searches/day`],
                        ["📋", plan.features?.maxReportsPerMonth === -1 ? "Unlimited reports"  : `${plan.features?.maxReportsPerMonth || 0} reports/month`],
                        ["📧", plan.features?.canReceiveAlerts ? "Email alerts" : null],
                        ["📄", plan.features?.canExportCSV     ? "CSV export" : null],
                        ["🖼️", plan.features?.canViewReportImages ? "Report images" : null],
                        ["📥", plan.features?.canBulkImport ? "Bulk import" : null],
                      ].filter(([, v]) => v).map(([icon, text]) => (
                        <div key={text} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#374151" }}>
                          <span>{icon}</span> {text}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* ── Payment section (shows after plan selected) ── */}
            {selectedPlan && (
              <>
                {/* Price summary */}
                <div style={{ background: "linear-gradient(135deg, #ede9fe, #f4f7fe)", borderRadius: 12, padding: "14px 18px", textAlign: "center", marginBottom: 20, border: "1.5px solid #4318FF20" }}>
                  <div style={{ fontSize: 13, color: "#7551FF", fontWeight: 600, marginBottom: 4 }}>
                    Selected: <b>{selectedPlan.name}</b>
                  </div>
                  <div style={{ fontSize: 32, fontWeight: 900, color: "#4318FF" }}>₹{planTotal}</div>
                  {gstEnabled && (
                    <div style={{ fontSize: 11, color: "#7551FF", marginTop: 2 }}>
                      ₹{planBase} + ₹{gstAmt} GST ({gstPercent}%)
                    </div>
                  )}
                </div>

                {/* Razorpay */}
                <button onClick={handleRazorpayPayment} disabled={rzpLoading}
                  style={{
                    width: "100%", padding: "15px 20px", borderRadius: 12, border: "none",
                    cursor: rzpLoading ? "not-allowed" : "pointer",
                    background: rzpLoading ? "#a3aed0" : "#16a34a",
                    color: "#fff", fontWeight: 800, fontSize: 15, marginBottom: 6,
                    boxShadow: rzpLoading ? "none" : "0 4px 16px rgba(22,163,74,0.3)",
                  }}>
                  {rzpLoading ? "Opening Payment Gateway..." : "💳 Pay Online — Instant Activation (Razorpay)"}
                </button>
                <p style={{ textAlign: "center", fontSize: 11, color: "#16a34a", fontWeight: 600, margin: "0 0 16px" }}>
                  Secure payment · Account activated immediately after payment
                </p>

                {/* OR divider */}
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                  <div style={{ flex: 1, height: 1, background: "#e0e5f2" }} />
                  <span style={{ fontSize: 12, color: "#a3aed0", fontWeight: 600 }}>OR PAY MANUALLY</span>
                  <div style={{ flex: 1, height: 1, background: "#e0e5f2" }} />
                </div>

                {/* Manual UPI/Cash */}
                <div style={{ background: "#f4f7fe", borderRadius: 12, padding: "14px 16px", marginBottom: 16, fontSize: 13, color: "#374151" }}>
                  <div style={{ fontWeight: 700, color: "#1b2559", marginBottom: 8 }}>How to pay manually:</div>
                  <div style={{ lineHeight: 2, color: "#6b7280" }}>
                    1. Pay <b>₹{planTotal}</b> via UPI / Cash to admin<br/>
                    2. Note your <b>Transaction ID / UTR number</b> from your payment app<br/>
                    3. Enter it below and click Submit<br/>
                    4. Admin will activate your account within <b>12 hours</b>
                  </div>
                </div>

                <label style={lbl}>Payment Method</label>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
                  {["GPay", "PhonePe", "Paytm", "UPI", "Cash"].map(m => (
                    <button key={m} onClick={() => setMethod(m)}
                      style={{
                        padding: "8px 16px", borderRadius: 8,
                        border: `2px solid ${method === m ? "#4318FF" : "#e0e5f2"}`,
                        background: method === m ? "#ede9fe" : "#fff",
                        color: method === m ? "#4318FF" : "#6b7280",
                        fontWeight: method === m ? 700 : 500,
                        fontSize: 13, cursor: "pointer",
                      }}>
                      {m}
                    </button>
                  ))}
                </div>

                <div style={{ background: "#f0fdf4", border: "1.5px solid #16a34a40", borderRadius: 12, padding: 16, marginBottom: 16 }}>
                  <label style={{ display: "block", fontSize: 13, fontWeight: 700, color: "#15803d", marginBottom: 8 }}>
                    🔑 Transaction ID / UPI Reference Number *
                  </label>
                  <input
                    style={{ width: "100%", padding: "12px 14px", borderRadius: 8, border: "2px solid #16a34a",
                      background: "#fff", fontSize: 16, fontWeight: 700, letterSpacing: 1,
                      outline: "none", boxSizing: "border-box" }}
                    placeholder="e.g. 607075320137"
                    value={txnId}
                    onChange={e => { setTxnId(e.target.value); setMsg({ type: "", text: "" }); }}
                    onKeyDown={e => e.key === "Enter" && handleSubmit()}
                  />
                  <p style={{ fontSize: 11, color: "#16a34a", margin: "6px 0 0", fontWeight: 600 }}>
                    📱 Find this in your payment app → Transaction History → UTR / Reference number
                  </p>
                </div>

                <button onClick={handleSubmit} disabled={submitting || !txnId.trim()}
                  style={{ width: "100%", padding: 14, borderRadius: 12, border: "none",
                    cursor: !txnId.trim() ? "not-allowed" : "pointer",
                    background: !txnId.trim() ? "#e0e5f2" : "#4318FF",
                    color: !txnId.trim() ? "#a3aed0" : "#fff",
                    fontWeight: 800, fontSize: 15 }}>
                  {submitting ? "Submitting..." : "Submit Transaction ID ✓"}
                </button>
              </>
            )}
          </div>
        )}

        {/* ── Pending re-submit section ── */}
        {isPending && (
          <div style={{ background: "#fff", borderRadius: 16, padding: 20, border: "1.5px solid #fde68a", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: "#92400e", marginBottom: 4 }}>🔄 Re-submit Transaction ID</h3>
            <p style={{ fontSize: 13, color: "#b45309", marginBottom: 16, marginTop: 0 }}>
              Submitted a wrong Transaction ID? You can re-submit below.
            </p>

            <label style={lbl}>Payment Method</label>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
              {["GPay", "PhonePe", "Paytm", "UPI", "Cash"].map(m => (
                <button key={m} onClick={() => setMethod(m)}
                  style={{
                    padding: "8px 16px", borderRadius: 8,
                    border: `2px solid ${method === m ? "#4318FF" : "#e0e5f2"}`,
                    background: method === m ? "#ede9fe" : "#fff",
                    color: method === m ? "#4318FF" : "#6b7280",
                    fontWeight: method === m ? 700 : 500,
                    fontSize: 13, cursor: "pointer",
                  }}>
                  {m}
                </button>
              ))}
            </div>

            <div style={{ background: "#f0fdf4", border: "1.5px solid #16a34a40", borderRadius: 12, padding: 16, marginBottom: 16 }}>
              <label style={{ display: "block", fontSize: 13, fontWeight: 700, color: "#15803d", marginBottom: 8 }}>
                🔑 Transaction ID / UPI Reference Number *
              </label>
              <input
                style={{ width: "100%", padding: "12px 14px", borderRadius: 8, border: "2px solid #16a34a",
                  background: "#fff", fontSize: 16, fontWeight: 700, letterSpacing: 1,
                  outline: "none", boxSizing: "border-box" }}
                placeholder="e.g. 607075320137"
                value={txnId}
                onChange={e => { setTxnId(e.target.value); setMsg({ type: "", text: "" }); }}
                onKeyDown={e => e.key === "Enter" && handleSubmit()}
              />
            </div>

            <button onClick={handleSubmit} disabled={submitting || !txnId.trim()}
              style={{ width: "100%", padding: 14, borderRadius: 12, border: "none",
                cursor: !txnId.trim() ? "not-allowed" : "pointer",
                background: !txnId.trim() ? "#e0e5f2" : "#4318FF",
                color: !txnId.trim() ? "#a3aed0" : "#fff",
                fontWeight: 800, fontSize: 15 }}>
              {submitting ? "Submitting..." : "Re-submit Transaction ID ✓"}
            </button>
          </div>
        )}

      </div>
    </OwnerLayout>
  );
};

const lbl = { display: "block", fontSize: 12, fontWeight: 600, color: "#1b2559", marginBottom: 8 };

export default OwnerSubscription;
